import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuthStore } from '@/stores/authStore';
import type { UserRole } from '@/api/auth.types';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: ReadonlyArray<UserRole>;
}

/**
 * Garde de route Phase 1 :
 * - phase 'idle' | 'hydrating' → splash le temps de la ré-hydratation
 * - phase 'anonymous' → redirect /login
 * - phase 'authenticated' + rôle non autorisé → /forbidden
 * - sinon → enfants
 */
export default function ProtectedRoute({
  children,
  requiredRoles,
}: ProtectedRouteProps): JSX.Element {
  const phase = useAuthStore((s) => s.phase);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (phase === 'idle' || phase === 'hydrating') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="grid min-h-screen place-items-center bg-pearl"
      >
        <div className="flex items-center gap-3 font-sans text-sm text-sub">
          <span
            aria-hidden
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gold-500 border-t-transparent"
          />
          Chargement de la session…
        </div>
      </div>
    );
  }

  // `!user` couvre null ET undefined : si une réponse auth omettait `user`,
  // l'ancien `user === null` laissait passer un undefined -> `user.role` plus
  // bas crashait en page blanche. On redirige proprement vers /login.
  if (phase === 'anonymous' || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requiredRoles !== undefined && requiredRoles.length > 0) {
    if (!requiredRoles.includes(user.role)) {
      return <Navigate to="/forbidden" replace />;
    }
  }

  return <>{children}</>;
}
